import { AddIcon, BinIcon, Button, EyeOnIcon } from "@jutro/ui";
import dayjs from "dayjs";
import { tv } from "tailwind-variants";
import {
  EdmMedicalAppShiftWithoutVisitFragment,
  EdmNonWorkingPeriod,
} from "lib/graphql/megaSchema";
import { PrivNfzStateIcon } from "views/StaffSchedule/StaffScheduleCreator/components/PrivNfzStateIcon";
import { PublicNfzStateIcon } from "views/StaffSchedule/StaffScheduleCreator/components/PublicNfzStateIcon";
import { isMedicalAppShift } from "views/StaffSchedule/StaffScheduleCreator/tools";

type ShiftProps = {
  shift: EdmMedicalAppShiftWithoutVisitFragment | EdmNonWorkingPeriod;
  selectedOrganizationId: string | undefined;
  isDeletable: boolean;
  openModal: (mode: "create" | "edit") => void;
};

const BLOCK_HEIGHT = "h-[104px]";
const BUTTON_HEIGHT = "max-h-10";

export const Block = ({
  shift,
  selectedOrganizationId,
  isDeletable,
  openModal,
}: ShiftProps) => {
  const firstVisitTypeFromShift =
    isMedicalAppShift(shift) && shift.visitTypes.length
      ? shift.visitTypes[0]
      : "DEFAULT";

  const isActiveOrganization =
    isMedicalAppShift(shift) && selectedOrganizationId === shift.organizationId;
  const isMultiShift = isMedicalAppShift(shift) && shift.visitTypes.length > 1;

  return (
    <div
      data-testId={shift.id}
      className={`${
        isMedicalAppShift(shift)
          ? getShiftStyles({
              activeOrganization: isActiveOrganization,
              visitType: firstVisitTypeFromShift,
              state: shift.state,
              multiShift: isMultiShift,
            })
          : getNonWorkingPeriodsStyles({ leaveType: shift.leaveType })
      }`}
    >
      {isDeletable ? (
        <div className="flex flex-1 items-center justify-center text-black">
          <BinIcon />
        </div>
      ) : (
        <div className="relative flex flex-1 flex-col items-center justify-center gap-2 py-1">
          {isMultiShift && (
            <div className="absolute top-1 right-1 flex h-[14px] w-[14px] items-center justify-center self-end rounded-full bg-white/50">
              <span className="font-caption">{shift.visitTypes.length}</span>
            </div>
          )}
          <div className="flex flex-col">
            <span className="rounded-lg text-center">{`${dayjs(shift.startFrom).format("HH:mm")}`}</span>
            <span className="rounded-lg text-center">{`${dayjs(shift.endedAt).format("HH:mm")}`}</span>
          </div>
          {isMedicalAppShift(shift) && getShiftNfzStatus(shift)}
        </div>
      )}

      <div
        className={`hover:border-jutro-new-warm-gray-300 absolute top-0 right-0 z-5 flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg bg-white p-1 opacity-0 hover:border hover:opacity-100`}
      >
        <div className={`flex w-full items-center ${BUTTON_HEIGHT}`}>
          <Button
            label="editSlot"
            icon={<EyeOnIcon size="sm" />}
            variant="ghost"
            withTooltip={false}
            size="condensed"
            onClick={() => openModal("edit")}
          />
        </div>
        <div className={`flex w-full items-center ${BUTTON_HEIGHT}`}>
          <Button
            label="addSlot"
            icon={<AddIcon size="sm" />}
            variant="ghost"
            withTooltip={false}
            size="condensed"
            onClick={() => openModal("create")}
          />
        </div>
      </div>
    </div>
  );
};

const getShiftNfzStatus = (shift: EdmMedicalAppShiftWithoutVisitFragment) => {
  if (shift.status === "ACCEPTING_NFZ") {
    return <PublicNfzStateIcon />;
  }

  if (shift.status === "ACCEPTING_PRIV") {
    return <PrivNfzStateIcon />;
  }

  return null;
};

const getShiftStyles = tv({
  base: `font-paragraph-1 relative flex ${BLOCK_HEIGHT} w-full flex-col overflow-hidden rounded-lg`,
  variants: {
    activeOrganization: {
      true: "border-none",
      false: "border border-black !bg-white hover:border-none",
    },
    multiShift: {
      true: "shadow-[inset_0_-8px_0_rgba(0,0,0,0.15)]",
      false: "",
    },
    visitType: {
      IN_PERSON: "",
      TELEMEDICAL: "",
      DOCTOR_TELEMEDICAL_INFECTION: "",
      NURSE_IN_PERSON_INFECTION: "",
      PROCEDURE: "",
      COLLECTION: "",
      DOCTOR_HOUSE_CALL: "",
      NURSE_HOUSE_CALL: "",
      RADIOLOGY_APPOINTMENT: "",
      PRESCRIPTION_APPOINTMENT: "",
      EXPRESS_APPOINTMENT: "",
      SICK_CHILDREN: "",
      SPECIALIST: "",
      DEFAULT: "",
      MIDWIFE_HOUSE_CALL: "",
      MIDWIFE_IN_PERSON: "",
      DIETETICS: "",
      HEALTHY_CHILDREN_CHECKUP: "",
      HEALTHY_CHILDREN_CHECKUP_AND_VACCINE: "",
      HEALTHY_CHILDREN_PATRONAGE: "",
      HEALTHY_CHILDREN_VACCINE: "",
      NURSE_IN_PERSON: "",
      NURSE_HEALTHY_CHILDREN: "",
    },
    state: {
      PUBLISHED: "",
      DRAFT: "",
      PRE_DELETED: "",
      DELETED: "",
    },
  },

  compoundVariants: [
    {
      visitType: "IN_PERSON",
      state: "PUBLISHED",
      className: "bg-[#C2D9FF]",
    },
    {
      visitType: "IN_PERSON",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C2D9FF,_#C2D9FF,_#D4E4FF_10px,_#D4E4FF_20px)]",
    },
    {
      visitType: "IN_PERSON",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C2D9FF,_#C2D9FF,_#D4E4FF_10px,_#D4E4FF_20px)]",
    },
    {
      visitType: "TELEMEDICAL",
      state: "PUBLISHED",
      className: "bg-[#FABFBA]",
    },
    {
      visitType: "TELEMEDICAL",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FABFBA,_#FABFBA,_#FBD2CF_10px,_#FBD2CF_20px)]",
    },
    {
      visitType: "TELEMEDICAL",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FABFBA,_#FABFBA,_#FBD2CF_10px,_#FBD2CF_20px)]",
    },
    {
      visitType: "DOCTOR_TELEMEDICAL_INFECTION",
      state: "PUBLISHED",
      className: "bg-[#A9E5D6]",
    },
    {
      visitType: "DOCTOR_TELEMEDICAL_INFECTION",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#A9E5D6,_#A9E5D6,_#C3EDE2_10px,_#C3EDE2_20px)]",
    },
    {
      visitType: "DOCTOR_TELEMEDICAL_INFECTION",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#A9E5D6,_#A9E5D6,_#C3EDE2_10px,_#C3EDE2_20px)]",
    },
    {
      visitType: "NURSE_IN_PERSON",
      state: "PUBLISHED",
      className: "bg-[#C2D9FF]",
    },
    {
      visitType: "NURSE_IN_PERSON",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C2D9FF,_#C2D9FF,_#D4E4FF_10px,_#D4E4FF_20px)]",
    },
    {
      visitType: "NURSE_IN_PERSON",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C2D9FF,_#C2D9FF,_#D4E4FF_10px,_#D4E4FF_20px)]",
    },
    {
      visitType: "PROCEDURE",
      state: "PUBLISHED",
      className: "bg-[#C8CAFC]",
    },
    {
      visitType: "PROCEDURE",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C8CAFC,_#C8CAFC,_#D8DAFD_10px,_#D8DAFD_20px)]",
    },
    {
      visitType: "PROCEDURE",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C8CAFC,_#C8CAFC,_#D8DAFD_10px,_#D8DAFD_20px)]",
    },
    {
      visitType: "COLLECTION",
      state: "PUBLISHED",
      className: "bg-[#FCD2E0]",
    },
    {
      visitType: "COLLECTION",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FCD2E0,_#FCD2E0,_#FDDFE9_10px,_#FDDFE9_20px)]",
    },
    {
      visitType: "COLLECTION",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FCD2E0,_#FCD2E0,_#FDDFE9_10px,_#FDDFE9_20px)]",
    },
    {
      visitType: "DOCTOR_HOUSE_CALL",
      state: "PUBLISHED",
      className: "bg-[#C6EAC2]",
    },
    {
      visitType: "DOCTOR_HOUSE_CALL",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C6EAC2,_#C6EAC2,_#D7F0D4_10px,_#D7F0D4_20px)]",
    },
    {
      visitType: "DOCTOR_HOUSE_CALL",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C6EAC2,_#C6EAC2,_#D7F0D4_10px,_#D7F0D4_20px)]",
    },
    {
      visitType: "NURSE_HOUSE_CALL",
      state: "PUBLISHED",
      className: "bg-[#C6EAC2]",
    },
    {
      visitType: "NURSE_HOUSE_CALL",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C6EAC2,_#C6EAC2,_#D7F0D4_10px,_#D7F0D4_20px)]",
    },
    {
      visitType: "NURSE_HOUSE_CALL",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C6EAC2,_#C6EAC2,_#D7F0D4_10px,_#D7F0D4_20px)]",
    },
    {
      visitType: "RADIOLOGY_APPOINTMENT",
      state: "PUBLISHED",
      className: "bg-[#C8CAFC]",
    },
    {
      visitType: "RADIOLOGY_APPOINTMENT",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C8CAFC,_#C8CAFC,_#D8DAFD_10px,_#D8DAFD_20px)]",
    },
    {
      visitType: "RADIOLOGY_APPOINTMENT",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C8CAFC,_#C8CAFC,_#D8DAFD_10px,_#D8DAFD_20px)]",
    },
    {
      visitType: "PRESCRIPTION_APPOINTMENT",
      state: "PUBLISHED",
      className: "bg-[#FCD2E0]",
    },
    {
      visitType: "PRESCRIPTION_APPOINTMENT",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FCD2E0,_#FCD2E0,_#FDDFE9_10px,_#FDDFE9_20px)]",
    },
    {
      visitType: "PRESCRIPTION_APPOINTMENT",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FCD2E0,_#FCD2E0,_#FDDFE9_10px,_#FDDFE9_20px)]",
    },
    {
      visitType: "EXPRESS_APPOINTMENT",
      state: "PUBLISHED",
      className: "bg-[#FCD2E0]",
    },
    {
      visitType: "EXPRESS_APPOINTMENT",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FCD2E0,_#FCD2E0,_#FDDFE9_10px,_#FDDFE9_20px)]",
    },
    {
      visitType: "EXPRESS_APPOINTMENT",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FCD2E0,_#FCD2E0,_#FDDFE9_10px,_#FDDFE9_20px)]",
    },
    {
      visitType: "SICK_CHILDREN",
      state: "PUBLISHED",
      className: "bg-[#C2D9FF]",
    },
    {
      visitType: "SICK_CHILDREN",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C2D9FF,_#C2D9FF,_#D4E4FF_10px,_#D4E4FF_20px)]",
    },
    {
      visitType: "SICK_CHILDREN",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C2D9FF,_#C2D9FF,_#D4E4FF_10px,_#D4E4FF_20px)]",
    },
    {
      visitType: "MIDWIFE_HOUSE_CALL",
      state: "PUBLISHED",
      className: "bg-[#C6EAC2]",
    },
    {
      visitType: "MIDWIFE_HOUSE_CALL",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C6EAC2,_#C6EAC2,_#D7F0D4_10px,_#D7F0D4_20px)]",
    },
    {
      visitType: "MIDWIFE_HOUSE_CALL",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C6EAC2,_#C6EAC2,_#D7F0D4_10px,_#D7F0D4_20px)]",
    },
    {
      visitType: "MIDWIFE_IN_PERSON",
      state: "PUBLISHED",
      className: "bg-[#C2D9FF]",
    },
    {
      visitType: "MIDWIFE_IN_PERSON",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C2D9FF,_#C2D9FF,_#D4E4FF_10px,_#D4E4FF_20px)]",
    },
    {
      visitType: "MIDWIFE_IN_PERSON",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#C2D9FF,_#C2D9FF,_#D4E4FF_10px,_#D4E4FF_20px)]",
    },
    {
      visitType: "SPECIALIST",
      state: "PUBLISHED",
      className: "bg-[#FCD2E0]",
    },
    {
      visitType: "SPECIALIST",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FCD2E0,_#FCD2E0,_#FDDFE9_10px,_#FDDFE9_20px)]",
    },
    {
      visitType: "SPECIALIST",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FCD2E0,_#FCD2E0,_#FDDFE9_10px,_#FDDFE9_20px)]",
    },
    {
      visitType: "HEALTHY_CHILDREN_CHECKUP",
      state: "PUBLISHED",
      className: "bg-[#FFE1A6]",
    },
    {
      visitType: "HEALTHY_CHILDREN_CHECKUP",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
    {
      visitType: "HEALTHY_CHILDREN_CHECKUP",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
    {
      visitType: "HEALTHY_CHILDREN_CHECKUP_AND_VACCINE",
      state: "PUBLISHED",
      className: "bg-[#FFE1A6]",
    },
    {
      visitType: "HEALTHY_CHILDREN_CHECKUP_AND_VACCINE",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
    {
      visitType: "HEALTHY_CHILDREN_CHECKUP_AND_VACCINE",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
    {
      visitType: "HEALTHY_CHILDREN_PATRONAGE",
      state: "PUBLISHED",
      className: "bg-[#FFE1A6]",
    },
    {
      visitType: "HEALTHY_CHILDREN_PATRONAGE",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
    {
      visitType: "HEALTHY_CHILDREN_PATRONAGE",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
    {
      visitType: "HEALTHY_CHILDREN_VACCINE",
      state: "PUBLISHED",
      className: "bg-[#FFE1A6]",
    },
    {
      visitType: "HEALTHY_CHILDREN_VACCINE",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
    {
      visitType: "HEALTHY_CHILDREN_VACCINE",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
    {
      visitType: "NURSE_IN_PERSON_INFECTION",
      state: "PUBLISHED",
      className: "bg-[#A9E5D6]",
    },
    {
      visitType: "NURSE_IN_PERSON_INFECTION",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#A9E5D6,_#A9E5D6,_#C3EDE2_10px,_#C3EDE2_20px)]",
    },
    {
      visitType: "NURSE_IN_PERSON_INFECTION",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#A9E5D6,_#A9E5D6,_#C3EDE2_10px,_#C3EDE2_20px)]",
    },
    {
      visitType: "NURSE_HEALTHY_CHILDREN",
      state: "PUBLISHED",
      className: "bg-[#FFE1A6]",
    },
    {
      visitType: "NURSE_HEALTHY_CHILDREN",
      state: "DRAFT",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
    {
      visitType: "NURSE_HEALTHY_CHILDREN",
      state: "PRE_DELETED",
      activeOrganization: true,
      className:
        "bg-[repeating-linear-gradient(45deg,_#FFE1A6,_#FFE1A6,_#FFEAC1_10px,_#FFEAC1_20px)]",
    },
  ],
});

const getNonWorkingPeriodsStyles = tv({
  base: `font-paragraph-1 relative flex ${BLOCK_HEIGHT} w-full flex-col items-center justify-center overflow-hidden rounded-lg py-2`,
  variants: {
    leaveType: {
      PAID_HOLIDAY: "bg-[#C9C6C5]",
      OVERTIME: "bg-[#C9C6C5]",
      BREAK: "bg-[#C9C6C5]",
      ILLNESS: "bg-[#C9C6C5]",
      OFFICE_VENTILATION: "bg-[#C9C6C5]",
      MEETING: "bg-[#C9C6C5]",
      UNPAID_HOLIDAY: "bg-[#F5F0F0]",
    },
  },
});
